// src/hooks/usePullToRefresh.ts
//
// Lightweight pull-to-refresh for non-scrollable surfaces like the
// Discover swipe deck. The deck has no vertical scroll by design,
// so we can claim the entire surface for the pull gesture without
// stealing scroll from the user.
//
// Behavior:
//   - User touches anywhere on the surface, drags downward
//   - Pull distance is clamped (rubber-band style) so it never
//     moves the deck very far
//   - Past the threshold, release fires the refresh callback
//   - Below threshold, release snaps back with no action
//
// Returns:
//   - bind: spread these handlers onto the trackable Box
//   - pullPx: 0..maxPullPx, used to render a pull indicator
//   - isPulling: true while finger is down and pulling

import { useCallback, useRef, useState } from 'react';

interface Options {
  onRefresh: () => void | Promise<void>;
  /** Pixel distance past which a release fires the refresh. */
  threshold?: number;
  /** Hard cap on visual pull distance. */
  maxPullPx?: number;
  /** Don't activate when the user is actively swiping cards. We
   *  detect "swipe" via horizontal travel exceeding vertical; pull
   *  only activates for clearly vertical drags. */
  horizontalGuardPx?: number;
  /** Disable when zero items present (the empty state has its own
   *  refresh button) or while a refresh is already inflight. */
  disabled?: boolean;
}

interface PullToRefreshBindings {
  onTouchStart: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchMove: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
}

export function usePullToRefresh(opts: Options): {
  bind: PullToRefreshBindings;
  pullPx: number;
  isPulling: boolean;
} {
  const {
    onRefresh,
    threshold = 80,
    maxPullPx = 110,
    horizontalGuardPx = 24,
    disabled = false,
  } = opts;

  const [pullPx, setPullPx] = useState(0);
  const [isPulling, setIsPulling] = useState(false);

  // Refs for live touch tracking — we don't want React state on
  // every move event because that would re-render at 60fps.
  const startYRef = useRef<number | null>(null);
  const startXRef = useRef<number | null>(null);
  // Once we've decided this drag is horizontal (a card swipe), we
  // ignore further moves. Avoids the user starting a swipe and
  // accidentally triggering a refresh when finger drifts down.
  const cancelledRef = useRef(false);

  const reset = useCallback(() => {
    startYRef.current = null;
    startXRef.current = null;
    cancelledRef.current = false;
    setIsPulling(false);
    setPullPx(0);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (disabled) return;
      const t = e.touches[0];
      if (!t) return;
      startYRef.current = t.clientY;
      startXRef.current = t.clientX;
      cancelledRef.current = false;
    },
    [disabled]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (disabled) return;
      if (cancelledRef.current) return;
      if (startYRef.current === null) return;
      const t = e.touches[0];
      if (!t) return;
      const dy = t.clientY - startYRef.current;
      const dx =
        startXRef.current !== null ? t.clientX - startXRef.current : 0;

      // Decisively horizontal? Cancel pull tracking — this is a
      // card swipe.
      if (Math.abs(dx) > horizontalGuardPx && Math.abs(dx) > Math.abs(dy)) {
        cancelledRef.current = true;
        setIsPulling(false);
        setPullPx(0);
        return;
      }

      // Only positive (downward) pulls count.
      if (dy <= 0) {
        if (isPulling) {
          setIsPulling(false);
          setPullPx(0);
        }
        return;
      }

      // Rubber-band: ease the pull as it approaches the cap so it
      // never feels like the page is being yanked.
      const eased = Math.min(maxPullPx, dy * 0.55);
      setPullPx(eased);
      if (!isPulling) setIsPulling(true);
    },
    [disabled, horizontalGuardPx, maxPullPx, isPulling]
  );

  const handleTouchEnd = useCallback(() => {
    if (disabled) return;
    if (cancelledRef.current || startYRef.current === null) {
      reset();
      return;
    }
    if (pullPx >= threshold) {
      // Past threshold — fire refresh. Visual indicator can stay
      // visible for a brief moment to acknowledge the action; we
      // reset state in the same tick because the parent will
      // typically swap content (loading spinner or new cards) and
      // the pull indicator overlapping that would look stale.
      try {
        const result = onRefresh();
        if (result && typeof (result as any).then === 'function') {
          (result as Promise<void>).catch(() => {
            /* swallow — caller surfaces errors */
          });
        }
      } catch {
        /* swallow */
      }
    }
    reset();
  }, [disabled, pullPx, threshold, onRefresh, reset]);

  return {
    bind: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: reset,
    },
    pullPx,
    isPulling,
  };
}
